import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import api from '../api';
import './ContactForm.css';

function ContactForm({ providerId, servizioId, providerName, messaggiAttivi = true }) {
  // I fornitori "placeholder" (creati dall'admin, senza un account reale agganciato)
  // non hanno ancora attivato i messaggi: si blocca l'invio con tooltip esplicativo.
  const messaggiDisattivati = messaggiAttivi === false;
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefono: '',
    oggetto: '',
    messaggio: '',
  });

  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [consenso, setConsenso] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (messaggiDisattivati) {
      setStatus('error');
      setErrorMessage('Questo fornitore non ha ancora attivato i messaggi su FesteDaPiccoli.');
      return;
    }

    // Validation
    if (!formData.nome.trim()) {
      setStatus('error');
      setErrorMessage('Il nome è obbligatorio');
      return;
    }

    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setStatus('error');
      setErrorMessage('Inserisci un email valido');
      return;
    }

    if (!formData.messaggio.trim()) {
      setStatus('error');
      setErrorMessage('Il messaggio è obbligatorio');
      return;
    }

    if (!consenso) {
      setStatus('error');
      setErrorMessage('Devi accettare la privacy policy per inviare il messaggio');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const payload = {
        provider_id: providerId,
        servizio_id: servizioId || null,
        mittente_nome: formData.nome,
        mittente_email: formData.email,
        mittente_telefono: formData.telefono || null,
        oggetto: formData.oggetto || 'Richiesta di informazioni',
        messaggio: formData.messaggio,
        consenso_privacy: consenso,
      };

      await api.post('/messaggi', payload);

      setStatus('success');
      setFormData({
        nome: '',
        email: '',
        telefono: '',
        oggetto: '',
        messaggio: '',
      });
      setConsenso(false);

      // Reset success after 5 seconds
      setTimeout(() => {
        setStatus('idle');
      }, 5000);
    } catch (error) {
      setStatus('error');
      setErrorMessage(
        error.response?.data?.error || 'Errore nell\'invio del messaggio. Riprova.'
      );
    }
  };

  return (
    <div className="contact-form-container">
      <div className="contact-form-header">
        <h3>Contatta {providerName}</h3>
        <p>Compila il form per inviare una richiesta di informazioni. L'eventuale risposta del fornitore arriverà all'email che inserisci qui sotto.</p>
      </div>

      <form onSubmit={handleSubmit} className="contact-form">
        {/* Name */}
        <div className="form-group">
          <label htmlFor="nome">Nome *</label>
          <input
            type="text"
            id="nome"
            name="nome"
            value={formData.nome}
            onChange={handleChange}
            placeholder="Il tuo nome"
            required
          />
        </div>

        {/* Email */}
        <div className="form-group">
          <label htmlFor="email">Email *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="La tua email"
            required
          />
        </div>

        {/* Phone */}
        <div className="form-group">
          <label htmlFor="telefono">Telefono</label>
          <input
            type="tel"
            id="telefono"
            name="telefono"
            value={formData.telefono}
            onChange={handleChange}
            placeholder="Il tuo numero (opzionale)"
          />
        </div>

        {/* Subject */}
        <div className="form-group">
          <label htmlFor="oggetto">Oggetto</label>
          <input
            type="text"
            id="oggetto"
            name="oggetto"
            value={formData.oggetto}
            onChange={handleChange}
            placeholder="Oggetto del messaggio"
          />
        </div>

        {/* Message */}
        <div className="form-group">
          <label htmlFor="messaggio">Messaggio *</label>
          <textarea
            id="messaggio"
            name="messaggio"
            value={formData.messaggio}
            onChange={handleChange}
            placeholder="Scrivi il tuo messaggio..."
            rows="5"
            required
          />
        </div>

        {/* Status Messages */}
        {status === 'success' && (
          <div className="status-message success">
            <CheckCircle size={18} />
            <span>Messaggio inviato con successo!</span>
          </div>
        )}

        {status === 'error' && (
          <div className="status-message error">
            <AlertCircle size={18} />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Consenso privacy (GDPR) */}
        <label className="consenso-privacy">
          <input
            type="checkbox"
            checked={consenso}
            onChange={(e) => setConsenso(e.target.checked)}
          />
          <span>
            Ho letto e accetto la{' '}
            <Link to="/privacy" target="_blank" rel="noreferrer">privacy policy</Link>.
          </span>
        </label>

        {/* Avviso sempre visibile quando il fornitore non ha attivato i messaggi.
            Necessario su mobile, dove il tooltto in hover del bottone non compare. */}
        {messaggiDisattivati && (
          <div className="status-message info messaggi-off">
            <AlertCircle size={18} />
            <span>Questo fornitore non ha ancora attivato i messaggi su FesteDaPiccoli.</span>
          </div>
        )}

        {/* Submit Button */}
        <div
          className={`btn-submit-wrap${messaggiDisattivati ? ' is-disabled' : ''}`}
          data-tooltip="Questo fornitore non ha ancora attivato i messaggi su FesteDaPiccoli."
          tabIndex={messaggiDisattivati ? 0 : undefined}
        >
          <button
            type="submit"
            className="btn-submit"
            disabled={status === 'loading' || messaggiDisattivati}
          >
            {status === 'loading' ? (
              <>
                <span className="spinner" />
                Invio in corso...
              </>
            ) : (
              <>
                <Send size={18} />
                Invia Messaggio
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ContactForm;
